/**
 * Finding the part a tour step lights. A change's step names a `data-tour`
 * attribute; a page tour may also name a block by its heading, a button by its
 * words, or a CSS selector — so a tour can point at a screen's own furniture
 * without every part carrying an attribute, and still survive real data.
 */

/** Visible: laid out, and not hidden by a phone's or a computer's layout. */
const shown = (element: Element) => {
  const rect = element.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

const words = (element: Element) =>
  ((element as HTMLElement).innerText ?? element.textContent ?? '').replace(/\s+/g, ' ').trim()

/**
 * The part a step names. `a || b` tries each in turn — a phone's layout
 * (cards, one pane at a time) where a computer's has a table or three panes.
 */
export function findTourTarget(target: string): Element | null {
  for (const each of target.split(' || ')) {
    const found = findOne(each.trim())
    if (found) return found
  }
  return null
}

function findOne(target: string): Element | null {
  const scope = document.querySelector('main') ?? document.body
  if (target.startsWith('block:')) {
    const heading = target.slice('block:'.length)
    return (
      [...scope.querySelectorAll('[data-slot="block"]')].find((block) => {
        const title = block.querySelector('h2, h3')
        return title !== null && words(title) === heading && shown(block)
      }) ?? null
    )
  }
  if (target.startsWith('button:')) {
    const label = target.slice('button:'.length)
    return (
      [...scope.querySelectorAll('button, a')].find(
        (button) =>
          shown(button) && (words(button) === label || button.getAttribute('aria-label') === label),
      ) ?? null
    )
  }
  if (target.startsWith('css:')) {
    return (
      [...document.querySelectorAll(target.slice('css:'.length))].find((element) =>
        shown(element),
      ) ?? null
    )
  }
  return document.querySelector(`[data-tour="${CSS.escape(target)}"]`)
}
